import { renderMarkdown } from "./markdown";
import {
  createDisplayIdRegex,
  displayIdKey,
  displayIdOffset,
  internalHref,
  internalLinkRef,
  type EntityReference,
} from "./references";

const PILL_CLASS = "entity-ref";
const NON_LINKABLE_ANCESTORS = new Set(["A", "CODE", "PRE"]);

function applyRefAttributes(
  anchor: HTMLAnchorElement,
  reference: EntityReference,
  label: string,
): void {
  anchor.classList.add(PILL_CLASS);
  anchor.setAttribute("data-ref-kind", reference.kind);
  anchor.setAttribute("data-ref-key", reference.key);
  anchor.setAttribute("data-ref-label", label);
}

/**
 * Convert an anchor pointing at an internal entity into a pill, in place.
 *
 * Anything that does not resolve to this app's own `/items/` or
 * `/documents/` route is left exactly as it is: a foreign host must not be
 * able to borrow the trusted styling of an internal reference, nor have its
 * `target`/`rel` stripped.
 */
function convertAnchor(anchor: HTMLAnchorElement): void {
  const reference = internalLinkRef(anchor.getAttribute("href") ?? "");
  if (!reference) return;
  // Internal navigation stays in the app, so a new browsing context is wrong.
  anchor.removeAttribute("target");
  anchor.removeAttribute("rel");
  anchor.setAttribute("href", internalHref(reference));
  applyRefAttributes(anchor, reference, anchor.textContent ?? "");
}

/**
 * Remove the reference-pill contract from every element in a parsed body.
 *
 * `renderMarkdown` used to keep `class` and `data-*`, so an author could write
 * `<a class="entity-ref" data-ref-key=… href="https://evil.example/">` and have
 * it render with the trusted internal-reference chrome under a label of their
 * choosing. FLOW-807's allow-list drops both before this function ever sees
 * them, so this strip is now belt-and-braces — it is what keeps the property
 * ("only this module mints a pill, and every pill's `href` is one it
 * validated") from depending on a sanitiser flag somewhere else.
 *
 * Two things about this are deliberate and easy to undo by accident.
 *
 * It is unconditional, not a validation pass. Keeping the contract on anchors
 * whose `href` happens to be internal would still let an author pair a
 * same-origin `/items/<uuid>` with an attacker-chosen `data-ref-key` and
 * `data-ref-label` — a pill reading "FLOW-42 · Fix login redirect" that
 * previews and opens something else entirely. Narrowing this to validation
 * reopens that spoof.
 *
 * It also means `renderChatMarkdown` does not trust its own output: it cannot
 * tell its markup from an author's, so rendering rendered HTML strips the pills
 * rather than preserving them. That lost idempotency is the price of the
 * property above, not a bug to fix by relaxing the strip. Each message body is
 * rendered once, from the raw content its author wrote.
 */
function stripPillContract(doc: Document): void {
  doc.body.querySelectorAll("*").forEach((element) => {
    element.classList.remove(PILL_CLASS);
    for (const attribute of [...element.attributes]) {
      if (attribute.name.startsWith("data-ref-")) {
        element.removeAttribute(attribute.name);
      }
    }
  });
}

function isLinkable(node: Node): boolean {
  for (let parent = node.parentElement; parent; parent = parent.parentElement) {
    if (NON_LINKABLE_ANCESTORS.has(parent.tagName)) return false;
  }
  return true;
}

/**
 * Replace display-ID matches in a text node with pill anchors. The pills have
 * no `href` — the entity's uuid is unknown until the reference is resolved —
 * so they carry `role`/`tabindex` to stay keyboard reachable.
 */
function linkifyTextNode(doc: Document, textNode: Text, regex: RegExp): void {
  const text = textNode.data;
  regex.lastIndex = 0;

  const fragment = doc.createDocumentFragment();
  let cursor = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    const start = displayIdOffset(match);
    const label = match[1];

    if (start > cursor) {
      fragment.appendChild(doc.createTextNode(text.slice(cursor, start)));
    }

    const anchor = doc.createElement("a");
    applyRefAttributes(
      anchor,
      { kind: "work_item", key: displayIdKey(label) },
      label,
    );
    anchor.setAttribute("role", "link");
    anchor.setAttribute("tabindex", "0");
    anchor.textContent = label;
    fragment.appendChild(anchor);

    cursor = start + label.length;
  }

  if (cursor === 0) return;
  if (cursor < text.length) {
    fragment.appendChild(doc.createTextNode(text.slice(cursor)));
  }
  textNode.replaceWith(fragment);
}

/**
 * Render chat message markdown to sanitised HTML, with entity references
 * (work items and documents) turned into interactive reference pills.
 *
 * Reference detection runs over the sanitised DOM, never over raw strings, so
 * nothing an author writes can escape into markup, and the pill contract is
 * stripped from the parsed body before any pill is minted, so nothing an
 * author writes can pass itself off as a reference either.
 */
export function renderChatMarkdown(content: string, prefix: string): string {
  const html = renderMarkdown(content);
  if (!html) return html;

  try {
    const doc = new DOMParser().parseFromString(html, "text/html");

    stripPillContract(doc);
    doc.body.querySelectorAll("a").forEach(convertAnchor);

    const displayIdRegex = createDisplayIdRegex(prefix);
    if (displayIdRegex) {
      const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
      const textNodes: Text[] = [];
      for (let node = walker.nextNode(); node; node = walker.nextNode()) {
        if (isLinkable(node)) textNodes.push(node as Text);
      }
      textNodes.forEach((node) => linkifyTextNode(doc, node, displayIdRegex));
    }

    return doc.body.innerHTML;
  } catch {
    return html;
  }
}
